import React, { useEffect, useState } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Modal, Select, TextField, Typography } from "@mui/material";
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import Button from 'packages/novous_web/src/common/components/Button';
import InputField from 'packages/novous_web/src/common/components/InputField';
import { countries } from './Countries';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import DOMPurify from 'dompurify';

interface ScheduledReport {
    firstName: string;
    username: string;
    password: string;
    authorities: string;
    phone: string;
    address: {
        fullAddress: string;
        country: string;
    };
    status: number;
}

const data: ScheduledReport[] = []

interface EditUserAccountProps {
    selectAccount: any;
    isEditModalOpen: boolean;
    handleEditModalClose: () => void;
    modalTitle: string;
    modalDescription: string;
    cancelButtonText: string;
    submitButtonText: string;
}

const EditUserAccount: React.FC<EditUserAccountProps> = ({
    isEditModalOpen,
    handleEditModalClose,
    selectAccount,
    modalTitle,
    modalDescription,
    cancelButtonText,
    submitButtonText
}) => {
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        userName: '',
        authorities: '',
        email: '',
        phone: '',
        address: {
            fullAddress: '',
            country: ''
        },
        currentPassword: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({
        firstName: '',
        userName: '',
        authorities: '',
        email: '',
        phone: '',
        address: {
            fullAddress: '',
            country: ''
        },
        currentPassword: '',
        password: '',
        confirmPassword: ''
    });

    const [isCurrentPasswordValid, setIsCurrentPasswordValid] = useState(false);

    useEffect(() => {
        if (selectAccount) {
            setFormData({
                firstName: selectAccount.firstName || '',
                userName: selectAccount.username || '',
                authorities: selectAccount.authorities || '',
                email: selectAccount.email || '',
                phone: selectAccount.phone || '',
                address: {
                    fullAddress: selectAccount.address?.fullAddress || '',
                    country: selectAccount.address?.country || ''
                },
                currentPassword: '',
                password: '',
                confirmPassword: ''
            });
        }
    }, [selectAccount]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        const sanitizedValue = DOMPurify.sanitize(value, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
        });

        if (name === 'currentPassword') {
            const isValid = value === selectAccount?.password;
            setIsCurrentPasswordValid(isValid);
            setErrors(prev => ({
                ...prev,
                currentPassword: isValid ? '' : 'Current password is incorrect'
            }));
        }

        if (name.startsWith('address.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [field]: sanitizedValue
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
        }
    }

    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [parentKey, childKey] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parentKey]: {
                    ...prev[parentKey as keyof typeof formData] as Partial<Record<string, unknown>>,
                    [childKey]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            firstName: '',
            userName: '',
            authorities: '',
            email: '',
            phone: '',
            address: {
                fullAddress: '',
                country: ''
            },
            currentPassword: '',
            password: '',
            confirmPassword: ''
        };

        // Validate all fields except Role
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Name is required';
            valid = false;
        }

        if (!formData.userName.trim()) {
            newErrors.userName = 'Username is required';
            valid = false;
        }

        if (!formData.authorities) {
            newErrors.authorities = 'Role is required';
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Contact number is required';
            valid = false;
        }

        if (!formData.address.country) {
            newErrors.address.country = 'Country is required';
            valid = false;
        }

        if (formData.currentPassword || formData.password || formData.confirmPassword) {
            if (!formData.currentPassword) {
                newErrors.currentPassword = 'Current password is required to change password';
                valid = false;
            } else if (!isCurrentPasswordValid) {
                newErrors.currentPassword = 'Current password is incorrect';
                valid = false;
            }

            if (!formData.password) {
                newErrors.password = 'Password is required';
                valid = false;
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
                valid = false;
            }

            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Please confirm your password';
                valid = false;
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
                valid = false;
            }
        }

        setErrors(newErrors);
        setIsFormValid(valid);
        return valid;
    };

    useEffect(() => {
        validateForm();
    }, [formData]);

    interface ScheduledReportResponse {
        data: {
            userList: ScheduledReport[]
        }
    }

    const {
        data: ScheduledReportData,
        error: ScheduledReportError,
        loading: ScheduledReportLoading,
        fetchData: ScheduledReportRequestData,
    } = useAxios<ScheduledReportResponse>(API_ENDPOINTS.users.USER_LIST_DATA, "POST");

    const {
        data: editUsersAccountSave,
        error: editUsersAccountSaveError,
        loading: editUsersAccountSaveLoading,
        fetchData: fetcheditUsersAccountSaveData,
    } = useApiError<any>(`${API_ENDPOINTS.users.CREATE_USER}`, "POST");
    console.log(formData, 'formData');
    const handelEditAccount = async () => {
        if (!validateForm()) return;

        setIsSendingEmail(true);

        const requestedData = {
            firstName: formData.firstName,
            userName: formData.userName,
            email: formData.email,
            phone: formData.phone,
            address: {
                fullAddress: formData.address.fullAddress,
                country: formData.address.country,
            },

            ...(formData.password && {
                password: formData.password,
                currentPassword: formData.currentPassword,
            }),
            loginUserId: user.id,
            id: selectAccount.id,
        };

        try {
            const response = await fetcheditUsersAccountSaveData(requestedData);

            if (response.status === 201 || response.status === 200) {
                setAlertMessage("User updated successfully");
                setAlertType("success");
                setAlertOpen(true);
                try {
                    await ScheduledReportRequestData({
                        userId: user.id,
                    });
                    handleEditModalClose();
                } catch (error) {
                    console.error("Error fetching updated user list:", error);
                    setAlertMessage("User updated but failed to refresh the list");
                    setAlertType("warning");
                    setAlertOpen(true);
                    handleEditModalClose();
                }
            } else {
                setAlertMessage(response?.error || "Failed to update user");
                setAlertType("error");
                setAlertOpen(true);
            }
        } catch (error) {
            setAlertMessage("An error occurred while updating the user");
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    return (
        <>
            <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            <Modal
                open={isEditModalOpen}
                onClose={handleEditModalClose}
                aria-labelledby="download-modal-title"
                aria-describedby="download-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        maxWidth: '880px',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                    }}
                >
                    <div className="flex flex-col">
                        <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary }}>
                            {modalTitle}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: (theme) => theme.palette.text.secondary,
                                fontSize: '14px',
                                lineHeight: '20px',
                                marginBottom: 4,
                            }}
                        >
                            {modalDescription}
                        </Typography>

                        <FormControl fullWidth>
                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <InputField
                                        label="Name"
                                        name="firstName"
                                        placeholder="Enter First Name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.firstName ? "error" : formData.firstName ? "success" : undefined}
                                        errorMessage={errors.firstName}
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputField
                                        label="Username"
                                        name="userName"
                                        placeholder="Enter User Name"
                                        value={formData.userName}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.userName ? "error" : formData.userName ? "success" : undefined}
                                        errorMessage={errors.userName}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <CustomDropDownInput
                                        label="Role"
                                        name="authorities"
                                        options={[{ value: formData.authorities, label: formData.authorities }]}
                                        value={formData.authorities}
                                        onChange={(selectedValue) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                authorities: selectedValue
                                            }));
                                        }}
                                        placeholder="Enter Email Address"
                                        isMandatory
                                        isDisabled={true}
                                        showDropdownArrow={true}
                                    />
                                    {errors.authorities && (
                                        <Typography variant="caption" color="error" className="mt-1 flex items-center">
                                            {errors.authorities}
                                        </Typography>
                                    )}
                                    {/* <Typography variant="h6" fontSize="14px" fontWeight={600} lineHeight='normal' letterSpacing='0%'>
                                        Role
                                    </Typography>
                                    <FormControl fullWidth sx={{ bgcolor: 'background.paper', borderRadius: '8px', marginTop: '0.8rem' }}>
                                        <Select
                                            name="authorities"
                                            value={formData.authorities}
                                            onChange={handleSelectChange}
                                            disabled
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    fontSize: '14px',
                                                    border: '1px solid #F2F2F2',
                                                    padding: '0.6rem',
                                                }
                                            }}
                                        >
                                            <MenuItem value={formData.authorities}>
                                                {formData.authorities}
                                            </MenuItem>
                                        </Select>
                                    </FormControl> */}
                                </div>
                                <div className="flex-1">
                                    <InputField
                                        label="Email Address"
                                        name="email"
                                        placeholder="Enter Email Address"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.email ? "error" : formData.email ? "success" : undefined}
                                        errorMessage={errors.email}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <InputField
                                        label="Contact Number"
                                        type='number'
                                        name="phone"
                                        placeholder="Enter Contact Number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.phone ? "error" : formData.phone ? "success" : undefined}
                                        errorMessage={errors.phone}
                                    />
                                </div>
                                <div className="flex-1">
                                    <CustomDropDownInput
                                        label="Country"
                                        name="address.country"
                                        options={countries.map(country => ({ value: country, label: country }))}
                                        value={formData.address.country}
                                        onChange={(selectedValue) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                address: {
                                                    ...prev.address,
                                                    country: selectedValue
                                                }
                                            }));
                                        }}
                                        placeholder="Select Country"
                                        isMandatory
                                        showDropdownArrow={true}
                                    />
                                    {errors.address.country && (
                                        <Typography variant="caption" color="error" className="mt-1 flex items-center">
                                            {errors.address.country}
                                        </Typography>
                                    )}
                                    {/* <Typography variant="h6" fontSize="14px" fontWeight={600} lineHeight='normal' letterSpacing='0%'>
                                        Country
                                    </Typography>
                                    <FormControl fullWidth sx={{ bgcolor: 'background.paper', borderRadius: '8px', marginTop: '0.8rem' }}>
                                        <Select
                                            name="address.country"
                                            value={formData.address.country}
                                            onChange={handleSelectChange}
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    fontSize: '14px',
                                                    border: '1px solid #F2F2F2',
                                                    padding: '0.6rem',
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={(theme) => ({
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                color: theme.palette.text.primary
                                            })}>
                                                Select Country
                                            </MenuItem>
                                            {countries.map((country) => (
                                                <MenuItem
                                                    key={country}
                                                    value={country}
                                                    sx={(theme) => ({
                                                        fontSize: '14px',
                                                        fontWeight: 400,
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.secondary.light,
                                                            borderRadius: '0'
                                                        }
                                                    })}
                                                >
                                                    {country}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {errors.address.country && (
                                        <Typography variant="caption" color="error" className="mt-1 flex items-center">
                                            {errors.address.country}
                                        </Typography>
                                    )} */}
                                </div>
                            </div>

                            <div className="mb-4">
                                <Typography variant="body2" fontWeight={600} color="textPrimary" className="self-start flex items-center space-x-1">
                                    <span>Address</span>
                                    <span className="text-red-500" title="Required field">*</span>
                                </Typography>

                                <textarea
                                    name="address.fullAddress"
                                    value={formData.address.fullAddress}
                                    onChange={handleChange}
                                    placeholder="Enter your message"
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        borderColor: 'background.paper',
                                        fontSize: '14px',
                                        outline: 'none',
                                        resize: 'vertical',
                                        marginTop: '8px',
                                    }}
                                    className='mt-2 p-3 text-black border border-gray-300'
                                />
                            </div>

                            <div className="mb-4">
                                <Typography variant="h6" fontSize="14px" fontWeight={600} lineHeight='normal' letterSpacing='0%'>
                                    Current Password (required to change password)
                                </Typography>
                                <InputField
                                    label="Current Password"
                                    name="currentPassword"
                                    placeholder="Enter Your Current Password"
                                    type="password"
                                    value={formData.currentPassword}
                                    onChange={handleChange}
                                    isMandatory
                                    status={
                                        errors.currentPassword ? "error" :
                                            formData.currentPassword ? (isCurrentPasswordValid ? "success" : undefined) : undefined
                                    }
                                    errorMessage={errors.currentPassword}
                                    isDisabled={!selectAccount?.password}
                                />
                            </div>

                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    <InputField
                                        label="Create Password"
                                        name="password"
                                        placeholder="Enter Your New Password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.password ? "error" : formData.password ? "success" : undefined}
                                        errorMessage={errors.password}
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputField
                                        label="Confirm Password"
                                        name="confirmPassword"
                                        placeholder="Confirm Your New Password"
                                        type="password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        isMandatory
                                        status={errors.confirmPassword ? "error" : formData.confirmPassword ? "success" : undefined}
                                        errorMessage={errors.confirmPassword}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 justify-between mt-8">

                                <CustomIconButton
                                    text={cancelButtonText}
                                    background="transparent" outline={true} padding="0px"
                                    variant="outlined"
                                    onClick={handleEditModalClose}
                                />

                                <Button
                                    text={submitButtonText}
                                    onClick={handelEditAccount}
                                    fullWidth={false}
                                    disabled={!isFormValid}
                                />
                            </div>
                        </FormControl>
                    </div>
                </Box>
            </Modal>
        </>
    );
}

export default EditUserAccount;